/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { useState } from "react";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "components/generals/Button";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { RoutesConstants } from "medicalsystem-shared";
import { UserInterface } from "medicalsystem-shared";
import PasswordRecoveryCard from "components/generals/PasswordRecoveryCard";
import FormInputPassword from "components/generals/FormInputPassword";
import { passwordValidation } from "utils/form.utils";
import { Formik, Form } from "formik";

/**
 * Properties for the ChangePassword component.
 * 
 * @interface ChangePasswordProps
 * @property {UserInterface} user - User account to reset password for
 */
interface ChangePasswordProps {
  user: UserInterface;
}

/**
 * Change password component for password recovery flow.
 * Final step allowing user to set new password with enhanced visibility controls.
 * 
 * Features:
 * - FormInputPassword component with show/hide toggle
 * - New password input with visibility control
 * - Confirmation password input with visibility control
 * - Password validation
 * - Match verification between password fields
 * - Security requirements enforcement
 * - Username comparison check
 * - Error messaging
 * - Loading state
 * - Navigation to login on success
 * - Formik form state management
 * - i18n translation support
 * 
 * Validation Rules:
 * - Required fields check
 * - Password confirmation match
 * - Password complexity requirements
 * - Username similarity check
 * - Enforced by passwordValidation utility
 * 
 * Workflow:
 * 1. User enters new password (can toggle visibility)
 * 2. User confirms password (can toggle visibility)
 * 3. User can view both passwords to verify they match
 * 4. Validates password requirements on submit
 * 5. Submits to API
 * 6. Redirects to login on success
 * 
 * @param {ChangePasswordProps} props - Component properties
 * @returns {JSX.Element} Password change form with Formik integration
 */
const ChangePassword = ({ user }: ChangePasswordProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState("");
  const [isLoading, , sendRequest] = useHttp();
  const navigate = useNavigate();

  const initialValues = {
    password: "",
    confirmPassword: "",
  };

  const handleChangePassword = (values: { password: string; confirmPassword: string }) => {
    setError("");

    if (!values.password || !values.confirmPassword) {
      return setError(t("passwordRecovery.requiredFields"));
    }

    if (values.password !== values.confirmPassword) {
      return setError(t("passwordRecovery.passwordsNotMatch"));
    }
    
    const result = passwordValidation(t, values.password, user.username);
    if (result !== true) {
      return setError(result);
    }
    sendRequest(
      CoreAPI_URLs.PASSWORD_RECOVERY_NEW_PASSWORD,
      () => navigate(RoutesConstants.LOGIN),
      "POST",
      JSON.stringify({ username: user.username, newPassword: values.password }),
      null,
      { applyErrorFn: (_message) => setError(t("passwordRecovery.errorChangingPassword")) }
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleChangePassword}
      enableReinitialize
    >
      {({ values, handleSubmit }) => (
        <Form>
          <PasswordRecoveryCard
            title={t("passwordRecovery.resetPassword")}
            error={error}
            footer={
              <Button
                variant="contained"
                color="info"
                onClick={() => handleSubmit()}
                disabled={isLoading}
              >
                {t("passwordRecovery.save")}
              </Button>
            }
          >
            <Stack spacing={2} width="100%">
              <FormInputPassword
                label={t("passwordRecovery.password")}
                name="password"
                fullWidth
                forceHidePassword={true}
              />
              <FormInputPassword
                label={t("passwordRecovery.confirmPassword")}
                name="confirmPassword"
                fullWidth
                forceHidePassword={true}
              />
            </Stack>
          </PasswordRecoveryCard>
        </Form>
      )}
    </Formik>
  );
};

export default ChangePassword;
